import { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  AlertCircle,
  Search,
  ArrowUpDown,
} from 'lucide-react';
import { useSource, useDataLoads } from '../../hooks/useSource';
import { summarizeNumber } from '../../services/formatting.service';
import type { DataLoad } from '../../services/source.service';
import { SourceIcon } from '../../components/SourceIcon';
import { SyncPollIndicator } from '../../components/sources/SyncPollIndicator';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

// pending    = not started yet
// extracting = being pulled from the source (extracted climbing, not yet loaded)
// extracted  = everything pulled (hit the expected total), waiting on the loader
// loading    = rows landing in the warehouse (loaded climbing)
// done       = fully loaded / sync finished
type StreamState = 'done' | 'loading' | 'extracted' | 'extracting' | 'pending';

type StreamProgress = {
  stream: string;
  extracted: number;
  loaded: number;
  expected: number | null;
  // 0–100 when we have an expected total; null otherwise (fallback: numbers only)
  pct: number | null;
  state: StreamState;
};

function formatDuration(ms: number): string {
  if (ms < 0 || !Number.isFinite(ms)) return '-';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

function statusBadge(status: string) {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
      return <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">Success</Badge>;
    case 'QUEUED':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-700 dark:text-yellow-400">Queued</Badge>;
    case 'SYNCING':
      return <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">Syncing</Badge>;
    case 'ERROR':
      return <Badge variant="destructive">Error</Badge>;
    case 'KILLED':
      return <Badge variant="outline" className="border-gray-500 text-gray-700 dark:text-gray-400">Killed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function buildStreamRows(load: DataLoad | undefined): StreamProgress[] {
  if (!load) return [];
  const extracted = load.stream_extracted_counts || {};
  const loaded = load.stream_load_counts || {};
  const expected = load.stream_total_counts || {};
  const active = ['QUEUED', 'SYNCING'].includes(load.status?.toUpperCase() ?? '');

  const names = new Set<string>([
    ...Object.keys(extracted),
    ...Object.keys(loaded),
    ...Object.keys(expected),
  ]);

  return [...names].map((stream) => {
    const x = extracted[stream] ?? 0;
    const l = loaded[stream] ?? 0;
    const e = stream in expected ? expected[stream] : null;
    const hasDenominator = e != null && e > 0;

    // The bar tracks the furthest-along count. Extraction leads loading, so this
    // gives live movement while pulling and never runs backward; it falls back to
    // loaded when a tap doesn't report extracted. Null pct = no expected → no bar.
    const basis = Math.max(x, l);
    const pct = hasDenominator ? Math.min(100, Math.round((basis / e!) * 100)) : null;

    let state: StreamState;
    if (l > 0 && (!hasDenominator || l >= e!)) {
      // fully loaded (or loaded with no usable denominator) → done. Don't keep
      // spinning just because the overall sync is still running other streams.
      state = 'done';
    } else if (l > 0) {
      // 0 < loaded < expected: writing to the warehouse
      state = active ? 'loading' : 'done';
    } else if (!active) {
      // sync is over and this stream loaded 0 rows — nothing left to do
      state = 'done';
    } else if (hasDenominator && x >= e!) {
      // extraction finished (pulled everything we expected) but the loader
      // hasn't landed rows yet — waiting, not working, so no spinner.
      state = 'extracted';
    } else if (x > 0 || e != null) {
      // rows are being pulled (or the stream has started) but nothing loaded yet
      state = 'extracting';
    } else {
      state = 'pending';
    }
    return { stream, extracted: x, loaded: l, expected: e, pct, state };
  });
}

function StreamStateIcon({ state }: { state: StreamState }) {
  // 'extracted' (everything pulled, awaiting the warehouse load) gets the same
  // green check as 'done': the stream's sync work is finished from the user's
  // point of view, so nothing should look in-flight.
  if (state === 'done' || state === 'extracted')
    return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  if (state === 'loading' || state === 'extracting')
    return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
  return <Circle className="h-4 w-4 text-muted-foreground/40" />;
}

export default function SyncDetail() {
  const { sourceId, dataloadId } = useParams<{ sourceId: string; dataloadId: string }>();
  const { data: source } = useSource(sourceId);
  const { data: loads, isLoading, refetch, dataUpdatedAt } = useDataLoads(sourceId);
  const load = useMemo(() => loads?.find((l) => l.id === dataloadId), [loads, dataloadId]);

  // matches useDataLoads' refetchInterval — how often the page polls while active
  const POLL_INTERVAL_MS = 10000;

  const [search, setSearch] = useState('');
  const [sortBySize, setSortBySize] = useState(true);

  const rows = useMemo(() => buildStreamRows(load), [load]);

  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter((r) => r.stream.toLowerCase().includes(term))
      : rows;
    const sorted = [...filtered].sort((a, b) => {
      if (sortBySize) {
        const av = a.expected ?? a.loaded;
        const bv = b.expected ?? b.loaded;
        return bv - av;
      }
      return a.stream.localeCompare(b.stream);
    });
    return sorted;
  }, [rows, search, sortBySize]);

  const stats = useMemo(() => {
    const totalStreams = rows.length;
    const doneStreams = rows.filter((r) => r.state === 'done').length;
    const totalExtracted = rows.reduce((s, r) => s + r.extracted, 0);
    const totalLoaded = rows.reduce((s, r) => s + r.loaded, 0);
    const totalExpected = rows.reduce((s, r) => s + (r.expected ?? 0), 0);
    // Not every stream reports extracted/expected metrics (e.g. QBO report
    // streams emit neither), so the sums above can cover different stream sets
    // than "loaded" does. Only treat them as comparable when every stream that
    // loaded rows also reported the metric — otherwise "68k loaded of ~4k".
    const expectedCoversLoaded =
      totalExpected > 0 && rows.every((r) => r.loaded === 0 || r.expected != null);
    const extractedCoversLoaded =
      totalExtracted > 0 && rows.every((r) => r.loaded === 0 || r.extracted > 0);
    return {
      totalStreams,
      doneStreams,
      totalExtracted,
      totalLoaded,
      totalExpected,
      expectedCoversLoaded,
      extractedCoversLoaded,
    };
  }, [rows]);

  const isActive = ['QUEUED', 'SYNCING'].includes(load?.status?.toUpperCase() ?? '');

  // Tick every second while syncing so Elapsed counts up smoothly, instead of
  // only jumping when the query refetches (~10s).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  // Prefer sync_started_at (when syncing actually began) so elapsed reflects sync
  // time, not time spent queued. Fall back to started_at for older/non-meltano loads.
  const syncStart = load?.sync_started_at ?? load?.started_at;

  const elapsedMs = useMemo(() => {
    if (!syncStart) return null;
    const start = new Date(syncStart).getTime();
    const end = load?.completed_at ? new Date(load.completed_at).getTime() : now;
    return end - start;
  }, [syncStart, load, now]);

  // Rough ETA from the whole-sync average rate. Deliberately conservative about
  // when to show: only while syncing, once we have an expected total and some
  // rows loaded, and past the first ~15s (rate is too noisy before that). It's an
  // estimate — rate is uneven across wide vs narrow tables and the expected total
  // grows as more streams report, so it will move around. Sampled off the data
  // refetch (not the 1s tick) so it doesn't creep upward between refetches.
  const etaMs = useMemo(() => {
    if (!isActive || !syncStart) return null;
    const sampledElapsed = Date.now() - new Date(syncStart).getTime();
    if (sampledElapsed < 15000) return null;
    if (!stats.expectedCoversLoaded || stats.totalLoaded <= 0) return null;
    const remaining = stats.totalExpected - stats.totalLoaded;
    if (remaining <= 0) return 0; // loaded has caught up to the estimate
    const rowsPerSec = stats.totalLoaded / (sampledElapsed / 1000);
    if (rowsPerSec <= 0) return null;
    return (remaining / rowsPerSec) * 1000;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, load, stats]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to={`/sources/${sourceId}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {source?.name ?? 'source'}
        </Link>

        <div className="flex items-center gap-3">
          {sourceId && <SourceIcon sourceId={sourceId} className="h-6 w-6" />}
          <h1 className="text-xl font-semibold">Sync details</h1>
          {load && statusBadge(load.status)}
          {load && isActive && (
            <SyncPollIndicator
              active={isActive}
              pollInterval={POLL_INTERVAL_MS}
              cycleKey={dataUpdatedAt}
              onCheckNow={async () => {
                await refetch();
              }}
            />
          )}
        </div>
      </div>

      {isLoading && !load && (
        <div className="flex items-center gap-2 py-10 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading sync…</span>
        </div>
      )}

      {!isLoading && !load && (
        <p className="text-sm text-muted-foreground py-10">
          This sync could not be found. It may have aged out of recent history.
        </p>
      )}

      {load && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
            <SummaryStat
              label="Streams"
              value={`${stats.doneStreams}/${stats.totalStreams}`}
              hint="complete"
            />
            {stats.extractedCoversLoaded ? (
              // Every loaded stream reported extraction counts, so the totals are
              // comparable: show pulled (climbs live) + loaded (lands at the
              // warehouse), so progress is visible before the load.
              <SummaryStat
                label="Records"
                valueNode={
                  <div className="space-y-0.5">
                    <div className="text-lg font-semibold tabular-nums">
                      {summarizeNumber(stats.totalExtracted)}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">extracted</span>
                    </div>
                    <div className="text-sm font-normal tabular-nums text-muted-foreground">
                      {summarizeNumber(stats.totalLoaded)} loaded
                    </div>
                  </div>
                }
                hint={stats.expectedCoversLoaded ? `of ~${summarizeNumber(stats.totalExpected)}` : undefined}
              />
            ) : (
              // Some (or all) loaded streams don't report extraction counts —
              // summing what's there would understate, so show loaded only.
              <SummaryStat
                label="Records loaded"
                value={summarizeNumber(stats.totalLoaded)}
                hint={stats.expectedCoversLoaded ? `of ~${summarizeNumber(stats.totalExpected)}` : undefined}
              />
            )}
            <SummaryStat
              label={isActive ? 'Elapsed' : 'Duration'}
              valueNode={
                <span className="flex items-center gap-1.5">
                  {isActive && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                  {elapsedMs != null ? formatDuration(elapsedMs) : '-'}
                </span>
              }
            />
            {isActive && (
              <SummaryStat
                label="Est. time left"
                value={
                  etaMs == null
                    ? 'N/A'
                    : etaMs <= 0
                      ? 'finishing…'
                      : `~${formatDuration(etaMs)}`
                }
                hint={etaMs != null && etaMs > 0 ? 'rough' : undefined}
              />
            )}
            <SummaryStat label="Status" valueNode={statusBadge(load.status)} />
          </div>

          {/* Overall progress bar — only when every loaded stream has an expected
              total, so the denominator actually covers what's loading */}
          {stats.expectedCoversLoaded && (
            <Progress
              value={Math.min(100, Math.round((stats.totalLoaded / stats.totalExpected) * 100))}
              variant={load.status?.toUpperCase() === 'SUCCESS' ? 'success' : 'default'}
            />
          )}

          {load.error_messages && load.error_messages.length > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-destructive text-sm font-medium">
                <AlertCircle className="h-4 w-4" /> Errors
              </div>
              {load.error_messages.map((msg, i) => (
                <p key={i} className="text-xs text-destructive/90 break-words">{msg}</p>
              ))}
            </div>
          )}

          {/* Per-stream progress */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search streams…"
                  className="pl-8 h-8"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => setSortBySize((v) => !v)}
              >
                <ArrowUpDown className="h-3.5 w-3.5" />
                {sortBySize ? 'By size' : 'By name'}
              </Button>
            </div>

            {filteredSorted.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6">
                {rows.length === 0
                  ? 'No per-stream progress reported yet.'
                  : 'No streams match your search.'}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[34%]">Stream</TableHead>
                    <TableHead className="w-[30%]">Progress</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Extracted</TableHead>
                    <TableHead className="text-right">Loaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSorted.map((r) => (
                    <TableRow key={r.stream}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <StreamStateIcon state={r.state} />
                          <span className="text-sm truncate">{r.stream}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.pct != null ? (
                          // real movement vs expected (extracted leads, then loaded)
                          <div className="flex items-center gap-2">
                            <Progress
                              value={r.pct}
                              variant={r.state === 'done' ? 'success' : 'default'}
                              className="h-1.5"
                            />
                            <span className="text-xs text-muted-foreground w-9 text-right">
                              {r.pct}%
                            </span>
                          </div>
                        ) : r.state === 'extracting' ? (
                          // pulling, no expected total → spinner, not a fake bar
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            extracting…
                          </span>
                        ) : r.state === 'loading' ? (
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            loading…
                          </span>
                        ) : r.state === 'done' ? (
                          // finished but no expected total was ever reported: we
                          // couldn't show partial progress along the way, but a
                          // completed stream shouldn't look unfinished — full bar.
                          <div className="flex items-center gap-2">
                            <Progress value={100} variant="success" className="h-1.5" />
                            <span className="text-xs text-muted-foreground w-9 text-right">
                              100%
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {r.expected != null ? `~${summarizeNumber(r.expected)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {r.extracted > 0 ? summarizeNumber(r.extracted) : '—'}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {summarizeNumber(r.loaded)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  valueNode,
  hint,
}: {
  label: string;
  value?: string;
  valueNode?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">
        {valueNode ?? value}
      </div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
